import { Action, type UserAccessProfile } from "@pilatestime/shared-types";
import { useLocation, useParams } from "@tanstack/react-router";
import type { LucideIcon } from "lucide-react";
import {
	BarChart3,
	ClipboardCheck,
	FileText,
	Gift,
	Receipt,
	Settings,
	ShieldCheck,
	User,
	Users,
} from "lucide-react";
import { can, getAllowedEstablishments } from "@/hooks/useAccessControl";

/**
 * Shared, permission-gated navigation contract for the org shell.
 *
 * Single source of truth for the org navigation surface (§5 of the Dashboard
 * Redesign plan). The sidebar, mobile bottom tabs and More page all render from
 * this config so the three presentations can never drift apart.
 *
 * The returned items are already filtered to what the current profile may see
 * and already carry their computed `isActive` state.
 */
export interface OrgNavItem {
	/** Stable identifier, used as React key and for placement lookups. */
	id: string;
	/** i18n key — the consumer is responsible for translating it. */
	labelKey: string;
	icon?: LucideIcon;
	/** Router destination. Absent for collapsible parent sections. */
	to?: string;
	params?: Record<string, string>;
	search?: Record<string, unknown>;
	/** Whether this item matches the current route. */
	isActive: boolean;
	/** The action gating this item, when it is a single-`can` gate (informational). */
	requiredAction?: Action;
	/** Where the item renders in the current shell (primary nav vs. footer). */
	placement?: "primary" | "footer";
	/** Rendered as an indented submenu entry inside a collapsible section. */
	isSubmenuItem?: boolean;
	children?: OrgNavItem[];
}

/**
 * Static description of a nav item, evaluated against a profile + location by
 * {@link useOrgNavigation}. Kept module-level so it is not rebuilt per render.
 */
interface OrgNavItemConfig {
	id: string;
	labelKey: string;
	icon?: LucideIcon;
	to?: string;
	search?: Record<string, unknown>;
	/** Path fragment fed to {@link isNavPathActive}; defaults to `to`. */
	activeMatch?: string;
	requiredAction?: Action;
	/** Custom visibility predicate for gates richer than a single `can`. */
	isVisible?: (profile: UserAccessProfile) => boolean;
	placement?: "primary" | "footer";
	isSubmenuItem?: boolean;
	children?: OrgNavItemConfig[];
}

/** True when the profile can act on the given action in at least one establishment. */
function hasEstablishmentAccess(
	profile: UserAccessProfile,
	action: Action,
): boolean {
	const allowed = getAllowedEstablishments(profile, action);
	return allowed === "ALL" || allowed.length > 0;
}

/**
 * Active-route matcher. Extracted verbatim from the previous hardcoded sidebar
 * so highlight behavior is preserved exactly. Exported for unit testing.
 */
export function isNavPathActive(
	path: string,
	currentPath: string,
	orgId: string,
): boolean {
	// Dashboard/Analytics - organization index page
	if (path === "/app/org/$orgId") {
		return currentPath === `/app/org/${orgId}`;
	}
	if (path.includes("attendance")) {
		return (
			currentPath.includes("/attendance") &&
			!currentPath.includes("/attendance-")
		);
	}
	if (path.includes("customers") && !path.includes("customer-plans")) {
		return (
			currentPath.includes("/customers") &&
			!currentPath.includes("/customer-plans")
		);
	}
	if (path.includes("enrollments")) {
		// Top-level enrollments section only (exclude customer enrollments and other prefixed enrollment routes)
		return (
			currentPath.includes("/enrollments") &&
			!currentPath.includes("/enrollment-") &&
			!currentPath.includes("/customers/")
		);
	}
	if (path.includes("customer-plans")) {
		return currentPath.includes("/customer-plans");
	}
	if (path.includes("establishments")) {
		return currentPath.includes("/establishments");
	}
	if (path.includes("staff")) {
		return currentPath.includes("/staff");
	}
	if (path.includes("schedules")) {
		return currentPath.includes("/schedules");
	}
	if (path.includes("birthday-reminder")) {
		return currentPath.includes("/birthday-reminder");
	}
	if (path.includes("enrollment-expiry-reminder")) {
		return currentPath.includes("/enrollment-expiry-reminder");
	}
	if (path.includes("settings")) {
		return currentPath.includes("/settings");
	}
	if (path.includes("org-management")) {
		return currentPath.includes("/org-management");
	}
	if (path.includes("billing")) {
		// Top-level billing section only (exclude customer billing tab, enrollment billing tab and settings billing)
		return (
			currentPath.includes("/billing") &&
			!currentPath.includes("/customers/") &&
			!currentPath.includes("/enrollments/") &&
			!currentPath.includes("/settings/")
		);
	}
	return currentPath === path;
}

/**
 * The org navigation config, in display order. Gating predicates and
 * active-match fragments mirror the previous hardcoded sidebar exactly.
 */
const ORG_NAV_CONFIG: OrgNavItemConfig[] = [
	{
		id: "dashboard",
		labelKey: "navigation.dashboard",
		icon: BarChart3,
		to: "/app/org/$orgId",
		activeMatch: "/app/org/$orgId",
	},
	{
		id: "attendance",
		labelKey: "navigation.attendance",
		icon: ClipboardCheck,
		to: "/app/org/$orgId/attendance",
		activeMatch: "/attendance",
		isVisible: (profile) =>
			hasEstablishmentAccess(profile, Action.VIEW_ATTENDANCE),
	},
	{
		id: "customers",
		labelKey: "navigation.customers",
		icon: User,
		to: "/app/org/$orgId/customers",
		search: { page: 1, limit: 10, search: "" },
		activeMatch: "/customers",
		requiredAction: Action.VIEW_CUSTOMERS,
	},
	{
		id: "enrollments",
		labelKey: "navigation.enrollment",
		icon: FileText,
		to: "/app/org/$orgId/enrollments",
		search: { page: 1, limit: 10 },
		activeMatch: "/enrollments",
		isVisible: (profile) =>
			hasEstablishmentAccess(profile, Action.VIEW_ENROLLMENTS),
	},
	{
		id: "billing",
		labelKey: "navigation.billing",
		icon: Receipt,
		to: "/app/org/$orgId/billing",
		activeMatch: "/billing",
		requiredAction: Action.MANAGE_PAYMENTS,
	},
	{
		id: "org-management",
		labelKey: "navigation.orgManagement",
		icon: ShieldCheck,
		to: "/app/org/$orgId/org-management",
		activeMatch: "/org-management",
		isVisible: (profile) =>
			can(profile, Action.VIEW_FINANCIAL_ANALYSIS) ||
			can(profile, Action.VIEW_CUSTOMER_RISK_ANALYSIS),
	},
	{
		id: "registrations",
		labelKey: "navigation.registrations",
		icon: Users,
		children: [
			{
				id: "establishments",
				labelKey: "establishments.management.title",
				to: "/app/org/$orgId/establishments",
				activeMatch: "/establishments",
				requiredAction: Action.VIEW_ESTABLISHMENTS,
				isSubmenuItem: true,
			},
			{
				id: "customer-plans",
				labelKey: "customerPlans.management.title",
				to: "/app/org/$orgId/customer-plans",
				activeMatch: "/customer-plans",
				requiredAction: Action.VIEW_CUSTOMER_PLANS,
				isSubmenuItem: true,
			},
			{
				id: "schedules",
				labelKey: "navigation.schedule",
				to: "/app/org/$orgId/schedules",
				activeMatch: "/schedules",
				isVisible: (profile) =>
					hasEstablishmentAccess(profile, Action.VIEW_SCHEDULES),
				isSubmenuItem: true,
			},
			{
				id: "staff",
				labelKey: "orgSettings.staff",
				to: "/app/org/$orgId/staff",
				activeMatch: "/staff",
				requiredAction: Action.MANAGE_STAFF,
				isSubmenuItem: true,
			},
		],
	},
	{
		id: "events",
		labelKey: "navigation.events",
		icon: Gift,
		isVisible: (profile) => can(profile, Action.MANAGE_SETTINGS),
		children: [
			{
				id: "birthday-reminder",
				labelKey: "navigation.birthdayEvents",
				to: "/app/org/$orgId/events/birthday-reminder",
				activeMatch: "/birthday-reminder",
				isSubmenuItem: true,
			},
			{
				id: "enrollment-expiry-reminder",
				labelKey: "navigation.enrollmentExpiryEvents",
				to: "/app/org/$orgId/events/enrollment-expiry-reminder",
				activeMatch: "/enrollment-expiry-reminder",
				isSubmenuItem: true,
			},
		],
	},
	{
		id: "settings",
		labelKey: "navigation.settings",
		icon: Settings,
		to: "/app/org/$orgId/settings",
		activeMatch: "/settings",
		requiredAction: Action.VIEW_SETTINGS,
		placement: "footer",
	},
];

/**
 * Resolves whether a config item is visible for the given profile.
 * `isVisible` takes precedence; otherwise a single `requiredAction` `can` gate;
 * otherwise the item is ungated (always visible).
 */
function isConfigVisible(
	config: OrgNavItemConfig,
	profile: UserAccessProfile,
): boolean {
	if (config.isVisible) {
		return config.isVisible(profile);
	}
	if (config.requiredAction) {
		return can(profile, config.requiredAction);
	}
	return true;
}

function resolveLeaf(
	config: OrgNavItemConfig,
	currentPath: string,
	orgId: string,
): OrgNavItem {
	return {
		id: config.id,
		labelKey: config.labelKey,
		icon: config.icon,
		to: config.to,
		params: { orgId },
		search: config.search,
		isActive: isNavPathActive(
			config.activeMatch ?? config.to ?? "",
			currentPath,
			orgId,
		),
		requiredAction: config.requiredAction,
		placement: config.placement,
		isSubmenuItem: config.isSubmenuItem,
	};
}

/**
 * Returns the permission-filtered, active-resolved org navigation items for the
 * current profile and route. Parent sections are dropped when none of their
 * children are visible; ungated sections (e.g. Registrations) surface iff at
 * least one child survives filtering.
 */
export function useOrgNavigation(profile: UserAccessProfile): OrgNavItem[] {
	const { orgId } = useParams({ from: "/_auth/app/_app/org/$orgId" });
	const { pathname } = useLocation();

	return ORG_NAV_CONFIG.reduce<OrgNavItem[]>((items, config) => {
		if (config.children) {
			const children = config.children
				.filter((child) => isConfigVisible(child, profile))
				.map((child) => resolveLeaf(child, pathname, orgId));

			// A section shows when its own gate passes AND it has visible children.
			if (isConfigVisible(config, profile) && children.length > 0) {
				items.push({
					id: config.id,
					labelKey: config.labelKey,
					icon: config.icon,
					isActive: children.some((child) => child.isActive),
					placement: config.placement,
					children,
				});
			}
			return items;
		}

		if (isConfigVisible(config, profile)) {
			items.push(resolveLeaf(config, pathname, orgId));
		}
		return items;
	}, []);
}
